import { Icon, Spinner } from "@blueprintjs/core";
import React, { useState } from "react";
import styles from "./CompletionWarningsDialog.module.scss";
import { CompletionWarning, groupWarnings, warningLink } from "./force-completion";

/** Сколько ошибок группы видно сразу: у «Проекта» их бывают сотни */
const VISIBLE = 5;

interface CompletionWarningListProps {
    warnings: CompletionWarning[];
    orderId: string;
    /** переход к месту ошибки; без него пункты — просто текст */
    onNavigate?: (link: string) => void;
}

/** Ошибки проверки перед завершением: вкладка — отдельный блок, пункт ведёт туда, где ошибку исправляют */
export const CompletionWarningList = ({ warnings, orderId, onNavigate }: CompletionWarningListProps) => {
    // нажатый пункт: переход открывает вкладку, а «Проект» с сотнями разделов рисуется не мгновенно —
    // сначала показываем индикатор, потом переходим
    const [pending, setPending] = useState<string | null>(null);

    const navigate =
        onNavigate &&
        ((key: string, link: string) => {
            if (pending) {
                return;
            }

            setPending(key);
            requestAnimationFrame(() =>
                setTimeout(() => {
                    onNavigate(link);
                    setPending(null);
                }, 30)
            );
        });

    return (
        <div className={styles.groups}>
            {groupWarnings(warnings).map((group) => (
                <WarningGroup
                    key={group.tab}
                    tab={group.tab}
                    label={group.label}
                    items={group.items}
                    orderId={orderId}
                    pending={pending}
                    onNavigate={navigate}
                />
            ))}
        </div>
    );
};

interface WarningGroupProps {
    tab: string;
    label: string;
    items: CompletionWarning[];
    orderId: string;
    /** нажатый пункт (`вкладка:номер`) */
    pending: string | null;
    onNavigate?: (key: string, link: string) => void;
}

const WarningGroup = ({ tab, label, items, orderId, pending, onNavigate }: WarningGroupProps) => {
    const [expanded, setExpanded] = useState(false);
    const shown = expanded ? items : items.slice(0, VISIBLE);
    const hidden = items.length - shown.length;

    return (
        <div className={styles.group}>
            <div className={styles.groupHeader}>
                <span className={styles.groupTitle}>{label}</span>
                <span className={styles.groupCount}>{items.length}</span>
            </div>

            {shown.map((warning, index) =>
                onNavigate ? (
                    <button
                        key={index}
                        type="button"
                        className={`${styles.item} ${styles.itemLink}`}
                        onClick={() => onNavigate(`${tab}:${index}`, warningLink(orderId, warning))}
                        disabled={!!pending}
                    >
                        <Icon icon="error" size={12} className={styles.itemIcon} />
                        <span className={styles.itemText}>{warning.text}</span>
                        {pending === `${tab}:${index}` ? (
                            <Spinner size={14} className={styles.itemPending} />
                        ) : (
                            <Icon icon="chevron-right" size={14} className={styles.itemArrow} />
                        )}
                    </button>
                ) : (
                    <div key={index} className={styles.item}>
                        <Icon icon="error" size={12} className={styles.itemIcon} />
                        <span className={styles.itemText}>{warning.text}</span>
                    </div>
                )
            )}

            {items.length > VISIBLE && (
                <button type="button" className={styles.more} onClick={() => setExpanded(!expanded)}>
                    {expanded ? "Свернуть" : `Показать ещё ${hidden}`}
                </button>
            )}
        </div>
    );
};
